import { useEffect, useMemo, useState } from 'react';
import { Tabs as TabsComponent, Typography, Wrapper } from '@screentone/core';
import isEqual from 'lodash.isequal';

import { HistoryContent, PublishHistory } from 'components/publish-history/PublishHistory';
import { SearchParams } from 'components/search-content/SearchContent';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  AvailableFeatureFlags,
  OffPlatformDto,
  OffPlatformListContainer,
  PublicationSetting,
  PublicationSettingSearchableContentType
} from 'data/generated/graphql';
import ContentSearch from 'features/off-platform-edit/components/off-platform-collection-curate-section/OffPlatformCollectionCurateSection';
import SettingsSection from 'features/off-platform-edit/components/off-platform-collection-settings-section/OffPlatformCollectionSettingsSection';
import { AllessehQuerySortKeyTypes } from 'hooks/useAllessehContentQuery';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { DEFAULT_CONTENT_SEARCH_FILTERS } from 'utils/constants';

interface MultiTabSectionProps {
  isInEditMode: boolean;
  isRecentlyPublish: boolean;
  isRecentlyUpdated: boolean;
  onUpdateName: (name: string) => void;
  handleShowHistoryEditMode: (historyItem: OffPlatformDto) => void;
  savedForScheduleEdit: OffPlatformDto | null;
  publicationSettings?: PublicationSetting;
}

enum Tabs {
  ContentSearch = 0,
  Embargoed = 1,
  PublishHistory = 2,
  Settings = 3
}
const MultiTabSection = ({
  isInEditMode,
  isRecentlyPublish,
  isRecentlyUpdated,
  onUpdateName,
  handleShowHistoryEditMode,
  savedForScheduleEdit,
  publicationSettings
}: MultiTabSectionProps) => {
  const { root: data, metadata, getDTO, getAllArticleIds } = useDataModelContext<OffPlatformListContainer>();
  const offPlatform = getDTO() as OffPlatformDto;
  const [liveVersion, setLiveVersion] = useState(offPlatform);
  const showEmbargoedTab = useFeatureFlag(AvailableFeatureFlags.ShowEmbargoedCollections, true);

  const [tabValue, setTabValue] = useState(Tabs.ContentSearch);

  useEffect(() => {
    setLiveVersion(offPlatform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecentlyPublish]);

  const usedIds = getAllArticleIds();

  const defaultFilterState = useMemo(
    () => ({
      ...DEFAULT_CONTENT_SEARCH_FILTERS,
      contentTypes: publicationSettings?.defaultContentTypes ?? [PublicationSettingSearchableContentType.Article],
      excludedContentIds: usedIds
    }),
    [usedIds, publicationSettings]
  );

  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: '',
    filters: defaultFilterState,
    sort: AllessehQuerySortKeyTypes.LiveDate
  });

  useEffect(() => {
    if (publicationSettings) {
      setSearchParams((currentParams) => ({
        ...currentParams,
        filters: {
          ...currentParams.filters,
          contentTypes: publicationSettings.defaultContentTypes
        }
      }));
    }
  }, [publicationSettings]);

  useEffect(() => {
    if (searchParams.filters.contentTypes.length > 0 && !isEqual(searchParams.filters.excludedContentIds, usedIds)) {
      setSearchParams((currentParams) => ({
        ...currentParams,
        filters: {
          ...currentParams.filters,
          excludedContentIds: usedIds
        }
      }));
    }
  }, [usedIds, searchParams]);

  const searchProps = {
    defaultFilterState,
    searchParams,
    onSearchParamsChange: setSearchParams
  };

  return (
    <Wrapper>
      <TabsComponent role="tablist" onChange={setTabValue} value={tabValue} margin={{ bottom: 'md' }}>
        <TabsComponent.Item
          role="tab"
          id="tab-id0"
          value={Tabs.ContentSearch}
          aria-selected={tabValue === Tabs.ContentSearch ? 'true' : 'false'}
          aria-controls="tabpanel-id0"
          data-testid="off-platform-content-search-tab"
        >
          Content Search
        </TabsComponent.Item>
        {showEmbargoedTab && (
          <TabsComponent.Item
            role="tab"
            id="tab-id1"
            value={Tabs.Embargoed}
            aria-selected={tabValue === Tabs.Embargoed ? 'true' : 'false'}
            aria-controls="tabpanel-id1"
          >
            Embargoed
          </TabsComponent.Item>
        )}
        <TabsComponent.Item
          role="tab"
          id="tab-id2"
          value={Tabs.PublishHistory}
          aria-selected={tabValue === Tabs.PublishHistory ? 'true' : 'false'}
          aria-controls="tabpanel-id2"
          data-testid="off-platform-publish-history"
        >
          Publish History
        </TabsComponent.Item>
        <TabsComponent.Item
          role="tab"
          id="tab-id3"
          value={Tabs.Settings}
          aria-selected={tabValue === Tabs.Settings ? 'true' : 'false'}
          aria-controls="tabpanel-id3"
        >
          Settings
        </TabsComponent.Item>
      </TabsComponent>
      {tabValue === Tabs.ContentSearch && (
        <Wrapper id="tabpanel-id0" role="tabpanel" aria-labelledby="tab-id0" padding={{ top: 'sm' }}>
          <Typography variant="header3" margin={{ bottom: 'none' }}>
            Content
          </Typography>
          <ContentSearch searchProps={searchProps} publicationSettings={publicationSettings} />
        </Wrapper>
      )}
      {tabValue === Tabs.Embargoed && (
        <Wrapper id="tabpanel-id1" role="tabpanel" aria-labelledby="tab-id1" padding={{ top: 'sm' }}>
          <Typography
            variant="header3"
            margin={{ bottom: 'none' }}
            data-testid="page-multi-tab-section-embargoed-tab-title"
          >
            Embargoed
          </Typography>
          <ContentSearch searchProps={searchProps} publicationSettings={publicationSettings} embargo />
        </Wrapper>
      )}
      {tabValue === Tabs.PublishHistory && (
        <Wrapper id="tabpanel-id2" role="tabpanel" aria-labelledby="tab-id2" padding={{ top: 'sm' }}>
          <PublishHistory<OffPlatformDto>
            liveVersion={liveVersion}
            publishUtc={0}
            hasRecentlyPublished={isRecentlyUpdated}
            originalVersionWhenInHistoryEditMode={savedForScheduleEdit}
            currentlyEditedVersion={isInEditMode ? offPlatform : undefined}
            handleShowHistoryEditMode={(historyItem: HistoryContent) =>
              handleShowHistoryEditMode(historyItem as OffPlatformDto)
            }
            // showURL={false}
            hidePreview
          />
        </Wrapper>
      )}
      {tabValue === Tabs.Settings && (
        <Wrapper id="tabpanel-id3" role="tabpanel" aria-labelledby="tab-id3" padding={{ top: 'sm' }}>
          <SettingsSection
            name={metadata.name}
            hostingURL={data.attributes.hostingPageURL}
            onUpdateName={onUpdateName}
            isInEditMode={isInEditMode}
          />
        </Wrapper>
      )}
    </Wrapper>
  );
};

export default MultiTabSection;
